/**
 * Verwaltung der Benutzeranfragen. Nur sichtbar/erreichbar mit der
 * Berechtigung "requests.manage" (siehe navItems.ts + App.tsx). Genehmigen
 * legt automatisch einen Benutzer mit der beantragten Rolle an
 * (ApproveRequestModal), Ablehnen protokolliert Bearbeiter + Zeitpunkt.
 */
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { ApproveRequestModal } from "./ApproveRequestModal";
import { notifyOpenRequestsChanged } from "../../hooks/useOpenRequestsCount";
import { formatDateTime } from "../../utils/formatDate";
import type { AccessRequest, PaginatedResult } from "../../types";

const STATUS_LABELS: Record<AccessRequest["status"], string> = {
  open: "Offen",
  approved: "Genehmigt",
  rejected: "Abgelehnt",
};
const STATUS_TONE: Record<AccessRequest["status"], "warning" | "success" | "danger"> = {
  open: "warning",
  approved: "success",
  rejected: "danger",
};

export default function AccessRequests() {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | AccessRequest["status"]>("");
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<AccessRequest | null>(null);

  const pageSize = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      const result = await api.get<PaginatedResult<AccessRequest>>(`/api/requests?${params}`);
      setRequests(result.items);
      setTotal(result.total);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleReject(r: AccessRequest) {
    if (!confirm(`Anfrage von "${r.full_name}" wirklich ablehnen?`)) return;
    try {
      await api.post(`/api/requests/${r.id}/reject`);
      load();
      notifyOpenRequestsChanged();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Ablehnen fehlgeschlagen.");
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Anfragen</h1>
        <p className="text-sm text-gray-500 mt-1">Zugangsanfragen prüfen, genehmigen oder ablehnen.</p>
      </div>

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            className="input sm:max-w-xs"
            placeholder="Suche nach Name…"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          />
          <div className="flex gap-2">
            {(["", "open", "approved", "rejected"] as const).map((s) => (
              <button
                key={s || "all"}
                onClick={() => { setPage(1); setStatusFilter(s); }}
                className={`px-3 py-1.5 rounded text-sm font-medium tracking-wide transition-colors ${
                  statusFilter === s ? "bg-accent-muted text-accent border border-accent/40" : "text-gray-400 hover:text-gray-200 border border-transparent"
                }`}
              >
                {s === "" ? "Alle" : STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card padded={false} className="overflow-x-auto">
        {loading ? (
          <Spinner />
        ) : requests.length === 0 ? (
          <EmptyState message="Keine Anfragen gefunden." />
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>Name</th>
                <th>Gewünschte Rolle</th>
                <th>Datum</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td className="font-medium text-gray-100">{r.full_name}</td>
                  <td><Badge tone="accent">{r.requested_role_name}</Badge></td>
                  <td className="text-gray-500 text-xs whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                  <td>
                    <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABELS[r.status]}</Badge>
                    {r.status === "approved" && r.approved_by_name && (
                      <span className="block text-gray-600 text-[11px] mt-0.5">von {r.approved_by_name}</span>
                    )}
                    {r.status === "rejected" && r.rejected_by_name && (
                      <span className="block text-gray-600 text-[11px] mt-0.5">von {r.rejected_by_name}</span>
                    )}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {r.status === "open" && (
                      <>
                        <button className="text-accent text-xs hover:underline mr-3" onClick={() => setApproving(r)}>Genehmigen</button>
                        <button className="text-danger text-xs hover:underline" onClick={() => handleReject(r)}>Ablehnen</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button className="btn-secondary px-3 py-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Zurück</button>
          <span className="text-gray-400">Seite {page} von {totalPages}</span>
          <button className="btn-secondary px-3 py-1" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Weiter</button>
        </div>
      )}

      {approving && (
        <ApproveRequestModal
          request={approving}
          onClose={() => setApproving(null)}
          onApproved={() => { setApproving(null); load(); notifyOpenRequestsChanged(); }}
        />
      )}
    </div>
  );
}
